import { SearchableSelect } from '@/components/ui/SearchableSelect';
import { useEmployeeSelectSearch } from '@/hooks/useEmployeeSelectSearch';
import type { EmployeeResponse } from '@/types/employee';
import { useSelectLabels } from '@/hooks/useSelectLabels';

export interface EmployeeSelectProps {
  label?: string;
  value?: string;
  onChange?: (value: string) => void;
  onEmployeeChange?: (employee: EmployeeResponse | null) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  activeOnly?: boolean;
  hrScoped?: boolean;
  required?: boolean;
  error?: string;
  hint?: string;
  showDetails?: boolean;
  name?: string;
  /** Omit these employee IDs from the dropdown (e.g. prevent self-manager). */
  excludeEmployeeIds?: number[];
  /** Omit the employee linked to this user id (e.g. hide logged-in user). */
  excludeUserId?: number;
  /** Load all company employees for approval delegation pickers. */
  approvalDelegateScope?: boolean;
  /** Page size for company employee list (default 30). */
  pageSize?: number;
  /** When false, the select is disabled (e.g. modal closed). */
  enabled?: boolean;
  /** Show a "No manager" style option to clear the selection. */
  allowClear?: boolean;
  clearLabel?: string;
}

/** Searchable employee picker — server search by code, name, or email. */
export function EmployeeSelect({
  label = 'Employee',
  value = '',
  onChange,
  onEmployeeChange,
  placeholder,
  disabled,
  className,
  activeOnly = true,
  hrScoped = false,
  required,
  error,
  hint,
  showDetails: _showDetails = true,
  name,
  excludeEmployeeIds,
  excludeUserId,
  approvalDelegateScope,
  pageSize,
  allowClear = false,
  clearLabel: clearLabelProp,
  enabled,
}: EmployeeSelectProps) {
  const selectLabels = useSelectLabels();
  const resolvedPlaceholder = placeholder ?? selectLabels.selectEmployee;
  const clearLabel = clearLabelProp ?? selectLabels.none;
  const { options, loading, searching, onSearchQuery, getEmployee } = useEmployeeSelectSearch({
    value,
    activeOnly,
    hrScoped,
    excludeEmployeeIds,
    excludeUserId,
    approvalDelegateScope,
    pageSize,
  });

  const handleChange = (next: string) => {
    onChange?.(next);
    onEmployeeChange?.(next ? getEmployee(next) ?? null : null);
  };

  return (
    <div className={className}>
      <SearchableSelect
        label={label}
        name={name}
        required={required}
        error={error}
        hint={hint}
        value={value}
        onChange={handleChange}
        options={options}
        placeholder={resolvedPlaceholder}
        disabled={disabled ?? (enabled === undefined ? false : !enabled)}
        loading={loading}
        searching={searching}
        searchPlaceholder={selectLabels.searchEmployee}
        emptyMessage={selectLabels.noEmployees}
        filterLocally={false}
        onSearchQuery={onSearchQuery}
        allowClear={allowClear}
        clearLabel={allowClear ? clearLabel : undefined}
      />
    </div>
  );
}
